import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { KeyValue } from '@/main/models/common';
import { supplierApi } from '@/main/api';
import { PaginationEnum } from '@/main/services/enum/PaginationEnum';
import { HeadQuarterBranchFilterEnum } from '@/main/services/enum/HeadQuarterBranchFilterEnum';
import {
  SaveSupplierCompanyRelationRequest,
  SupplierFilterRequest,
  SupplierInfoList
} from '@/main/models/request/supplierRequest';
import { SupplierRegistrationDataResponse } from '@/main/models/response';
import {
  getDefaultMessageResultsFound,
  getTranslateByKey,
  removeEmptyValues
} from '@/main/services/utils';
import KogModalSideComponent from '@/main/components/modal/ModalSide';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import useAsyncModalConfirmReason from '@/main/components/shared/asyncModalConfirmReason/asyncModalConfirmReason';
import { ReasonTypeEnum } from '@/main/services/enum/ReasonEnum';
import { toBase64 } from '@/main/services/utilsFile';
import { makeStyles } from '@mui/styles';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { toast } from 'react-toastify';
import KogAutoCompleteControlled from '../../inputAutoComplete/autoCompleteControlled';
import KogInputTextControlledNew from '../../inputText/inputTextControlledNew';
import KOGBoxGridCheckboxInverted from '../../boxGridCheckboxInverted/boxGridCheckboxInverted';
import * as S from './styles';
import { KOGAreaButtons } from '../../styles';

const useStyles = makeStyles(theme => ({
  header: {
    backgroundColor: theme.palette.common.white
  },
  bodyFilter: {
    backgroundColor: theme.palette.common.white
  },
  bodyRegister: {
    backgroundColor: theme.palette.common.white
  },
  detail: {
    display: 'flex',
    flexDirection: 'column'
  }
}));

interface Props {
  open?: any;
  closeModal?: any;
  reasonOptions?: any;
  distributionRegionReducer?: any;
  supplierTypeReducer?: any;
  manufacturerReducer?: any;
  watcherCountry?: any;
  guidBodyshop?: any;
  guidInsure?: any;
  resetGrid?: any;
  isInsurerView?: boolean;
}

const INITIAL_GRID_STATE: IGridData = {
  totalItems: 0,
  isLoading: false,
  data: []
};

interface IGridData {
  data: SupplierRegistrationDataResponse[];
  totalItems: number;
  isLoading: boolean;
}

export interface ISearchRestrictedAndFavoriteForm {
  supplierName: string | null;
  rfc: string | null;
  supplierType: KeyValue | null;
  make: KeyValue | null;
  region: KeyValue | null;
}

function MySuppliersRegister({
  open,
  closeModal,
  distributionRegionReducer,
  supplierTypeReducer,
  manufacturerReducer,
  watcherCountry,
  guidBodyshop,
  guidInsure,
  resetGrid,
  isInsurerView
}: Props) {
  const classes = useStyles();
  const [gridData, setGridData] = useState<IGridData>(INITIAL_GRID_STATE);
  const titleGrid = getDefaultMessageResultsFound(
    gridData.data.length,
    gridData.totalItems
  );
  const [isAccordionFilterOpen, setIsAccordionFilterOpen] = useState(true);
  const [isAccordionRegisterOpen, setIsAccordionRegisterOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState<SupplierInfoList[]>([]);
  const [getConfirmationReason, ModalConfirmationReason] = useAsyncModalConfirmReason();
  const [formFilters, setFormFilters] = useState<SupplierFilterRequest | null>(
    null
  );
  const [hasShowMore, setHasShowMore] = useState<boolean>(false);
  const [pageIndex, setPageIndex] = useState<number>(
    PaginationEnum.PageIndexIntial
  );
  const [isLoadingInfiniteScroll, setIsLoadingInfiniteScroll] = useState<
    boolean
  >(false);

  const INITIAL_FORM_STATE_SEARCH = {
    supplierName: '',
    rfc: '',
    supplierType: null as KeyValue | null,
    make: null as KeyValue | null,
    region: null as KeyValue | null
  };
  type IBlacklistFormSearch = typeof INITIAL_FORM_STATE_SEARCH;
  const methodSearch = useForm<IBlacklistFormSearch>({
    mode: 'onChange',
    defaultValues: INITIAL_FORM_STATE_SEARCH
  });

  const INITIAL_FORM_STATE_SAVE = {
    reason: null as KeyValue | null,
    rfc: ''
  };
  type IBlacklistFormSave = typeof INITIAL_FORM_STATE_SAVE;
  const methodSave = useForm<IBlacklistFormSave>({
    mode: 'onChange',
    defaultValues: INITIAL_FORM_STATE_SAVE
  });

  const methodGrid = useForm({
    mode: 'onChange',
    defaultValues: {
      refundLimit: {}
    }
  });

  const rowHeaders = [
    `${getTranslateByKey('information.rfc')}/${getTranslateByKey(
      'screen.supplier'
    )}`
  ];

  function onSubmitSearch(formInputs: ISearchRestrictedAndFavoriteForm) {
    setGridData({
      isLoading: true,
      totalItems: 0,
      data: []
    });

    const requestForm: SupplierFilterRequest = {
      guidCountry: String(watcherCountry.key),
      guidBodyshop: String(guidBodyshop) ? String(guidBodyshop) : null,
      guidInsurer: String(guidInsure) ? String(guidInsure) : null,
      companyName: formInputs.supplierName,
      registrationNumber: formInputs.rfc,
      idSupplierType: formInputs.supplierType?.key
        ? Number(formInputs.supplierType?.key)
        : null,
      guidVehicleManufacturer: formInputs.make?.key
        ? String(formInputs.make?.key)
        : null,
      guidDistributionRegion: formInputs.region?.key
        ? String(formInputs.region?.key)
        : null,
      type: HeadQuarterBranchFilterEnum.All,
      pageIndex: PaginationEnum.PageIndexIntial,
      pageSize: PaginationEnum.PageSize15
    };

    const requestFormatted = removeEmptyValues(requestForm);
    setFormFilters(requestFormatted);
    supplierApi
      .getGridRestrictedAndFavorite(requestFormatted)
      .then(response => {
        setGridData({
          isLoading: false,
          totalItems: response.data.totalNumberOfRecords,
          data: response.data.results
        });
        setHasShowMore(
          response.data.results.length < response.data.totalNumberOfRecords
        );
        setPageIndex(PaginationEnum.PageIndexIntial);
        setIsAccordionRegisterOpen(true);
        setIsAccordionFilterOpen(false);
      });
  }

  function onLoadInfiniteScroll(request: SupplierFilterRequest) {
    setIsLoadingInfiniteScroll(true);
    supplierApi
      .getGridRestrictedAndFavorite(request)
      .then(response => {
        let resultData: SupplierRegistrationDataResponse[] = [];
        let resultTotalItems: number = 0;
        if (
          response.data &&
          response.data.results &&
          response.data.results.length > 0
        ) {
          resultData = response.data.results;
          resultTotalItems = response.data.totalNumberOfRecords;
        }
        if (gridData.data.length > 0) {
          resultData = [...gridData.data, ...resultData];
        }
        setGridData({
          isLoading: false,
          totalItems: resultTotalItems,
          data: resultData
        });

        // Disable show more
        if (
          resultTotalItems < PaginationEnum.PageSize15 ||
          resultTotalItems === resultData.length
        ) {
          setHasShowMore(false);
        }
      })
      .finally(() => setIsLoadingInfiniteScroll(false));
  }

  function handleClear() {
    methodSearch.reset(INITIAL_FORM_STATE_SEARCH);
    methodSearch.setValue('supplierName', '');
    methodSearch.setValue('rfc', '');
    methodSearch.setValue('supplierType', null);
    methodSearch.setValue('make', null);
    methodSearch.setValue('region', null);
    methodGrid.reset({});
    setGridData(INITIAL_GRID_STATE);
    setFormFilters(null);
    setPageIndex(PaginationEnum.PageIndexIntial);
    setHasShowMore(false);
    setIsLoadingInfiniteScroll(false);
    setSelectedItems([]);
  }

  async function onSubmitSave() {
    if (selectedItems.length > 0) {
      let resultConfirm = null;
      let filesRequest = undefined;

      if (guidInsure || guidBodyshop) {
        resultConfirm = await getConfirmationReason(
          getTranslateByKey('information.confirmChangeTitle'),
          ReasonTypeEnum.ChangeCompany
        );

        if (!resultConfirm.confirmed) return;

        if (resultConfirm.files && resultConfirm.files.length > 0) {
          filesRequest = await Promise.all(
            resultConfirm.files.map(async item => {
              const base64DataUrl = await toBase64(item);
              return {
                fileName: item.name,
                contentType: item.type,
                fileBase64: base64DataUrl as string
              };
            })
          );
        }
      }

      const requestFormatted: SaveSupplierCompanyRelationRequest = {
        supplierInfoList: selectedItems,
        guidBodyShop: guidBodyshop || null,
        guidInsurer: guidInsure || null,
        guidReason: resultConfirm?.reason ? String(resultConfirm.reason.key) : undefined,
        reasonDescription: resultConfirm?.observation || '',
        favorite: true,
        restricted: false
      };

      if (guidInsure || guidBodyshop) {
        requestFormatted.changeJustification = {
          guidReason: resultConfirm?.reason ? String(resultConfirm.reason.key) : null,
          reasonDescription: resultConfirm?.observation || null,
          filesUpload: filesRequest
        };
      }

      supplierApi
        .saveSupplierCompanyRelation(requestFormatted)
        .then(() => {
          resetGrid();
          toast.success(
            getTranslateByKey('information.mySuppliersSavedSuccessfully')
          );
          closeModal();
          handleClear();
        })
        .catch(() => {});
    }
  }

  return (
    <>
      {open && (
        <KogModalSideComponent
          open={open}
          closeModal={() => closeModal()}
          id="id-mysuppliers-modal-scroll"
          titleModalReducer={getTranslateByKey(
            'information.registerMySuppliers'
          )}
          title={
            <S.Title>
              {getTranslateByKey('information.registerMySuppliers')}
            </S.Title>
          }
          width="700px">
          <Accordion
            expanded={isAccordionFilterOpen}
            onChange={(_, isExpanded) => setIsAccordionFilterOpen(isExpanded)}
            className={classes.bodyFilter}
            elevation={0}>
            <AccordionSummary
              className={classes.header}
              expandIcon={<S.IconExpandMore />}
              aria-controls="panel1bh-content"
              id="panel1bh-header">
              <S.AccordionTitle>
                {getTranslateByKey('information.filters')}
              </S.AccordionTitle>
            </AccordionSummary>
            <AccordionDetails>
              <form
                onSubmit={methodSearch.handleSubmit(onSubmitSearch)}
                noValidate
                style={{ width: '100%' }}>
                <KogInputTextControlledNew
                  id="id-mySuppliersRegister-text-supplierName"
                  name="supplierName"
                  label={getTranslateByKey('information.supplierName')}
                  control={methodSearch.control}
                  style={{ marginBottom: 10 }}
                />
                <KogInputTextControlledNew
                  id="id-mySuppliersRegister-text-rfc"
                  name="rfc"
                  label={getTranslateByKey('information.rfc')}
                  control={methodSearch.control}
                  style={{ marginBottom: 10 }}
                />
                <KogAutoCompleteControlled
                  className="ignore-click"
                  id="id-mySuppliersRegister-dropdown-supplierType"
                  label={getTranslateByKey('information.supplierType')}
                  nameCustom="supplierType"
                  errorMessageCustom=""
                  control={methodSearch.control}
                  setValueCustom={methodSearch.setValue}
                  options={supplierTypeReducer.supplierType.data}
                  isLoading={supplierTypeReducer.supplierType.isLoading}
                  rulesCustom={{ required: false }}
                  hasErrorCustom={false}
                  onChangeCustom={() => {}}
                  style={{ marginBottom: 10 }}
                />
                <KogAutoCompleteControlled
                  className="ignore-click"
                  id="id-mySuppliersRegister-dropdown-make"
                  label={getTranslateByKey('information.make')}
                  nameCustom="make"
                  errorMessageCustom=""
                  control={methodSearch.control}
                  setValueCustom={methodSearch.setValue}
                  options={manufacturerReducer.manufacturers.data}
                  isLoading={manufacturerReducer.manufacturers.isLoading}
                  rulesCustom={{ required: false }}
                  hasErrorCustom={false}
                  onChangeCustom={() => {}}
                  style={{ marginBottom: 10 }}
                />
                <KogAutoCompleteControlled
                  className="ignore-click"
                  id="id-mySuppliersRegister-dropdown-regionName"
                  label={getTranslateByKey('information.regionName')}
                  nameCustom="region"
                  errorMessageCustom=""
                  control={methodSearch.control}
                  setValueCustom={methodSearch.setValue}
                  options={distributionRegionReducer.distributionRegion.data}
                  isLoading={
                    distributionRegionReducer.distributionRegion.isLoading
                  }
                  rulesCustom={{ required: false }}
                  hasErrorCustom={false}
                  onChangeCustom={() => {}}
                  style={{ marginBottom: 10 }}
                />
                <KOGAreaButtons className="d-flex justify-content-center">
                  <SecondaryButton
                    id="id-mySuppliersRegister-button-clear"
                    onClick={handleClear}>
                    {getTranslateByKey('information.clear')}
                  </SecondaryButton>
                  <SecondaryButton
                    id="id-mySuppliersRegister-button-search"
                    type="submit">
                    {getTranslateByKey('information.search')}
                  </SecondaryButton>
                </KOGAreaButtons>
              </form>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={isAccordionRegisterOpen}
            onChange={(_, isExpanded) => setIsAccordionRegisterOpen(isExpanded)}
            className={classes.bodyRegister}
            elevation={0}>
            <AccordionSummary
              className={classes.header}
              expandIcon={
                gridData.isLoading ? <S.Spinner /> : <S.IconExpandMore />
              }
              aria-controls="panel2bh-content"
              id="panel2bh-header">
              <S.AccordionTitle>
                {getTranslateByKey('information.register')}
              </S.AccordionTitle>
            </AccordionSummary>
            <AccordionDetails className={classes.detail}>
              <S.TitleGrid>{titleGrid}</S.TitleGrid>
              <form onSubmit={methodSave.handleSubmit(onSubmitSave)} noValidate>
                {gridData.totalItems > 0 && (
                  <>
                    <KOGBoxGridCheckboxInverted
                      isLoadingInfiniteScroll={isLoadingInfiniteScroll}
                      pageIndex={pageIndex}
                      setPageIndex={setPageIndex}
                      hasShowMore={hasShowMore}
                      formFilters={formFilters}
                      setFormFilters={setFormFilters}
                      getGridData={(request: SupplierFilterRequest) => {
                        onLoadInfiniteScroll(request);
                      }}
                      rowHeaders={rowHeaders}
                      options={gridData.data}
                      optionsSelected={selectedItems}
                      setOptionsSelected={setSelectedItems}
                      onChangeCustom={optionsSelected => {
                        setSelectedItems(optionsSelected);
                      }}
                      idScrollTargetGrid="id-mysuppliers-modal-scroll"
                      showRefundLimitColumn={isInsurerView}
                      control={methodGrid.control}
                    />
                    <div className="d-flex justify-content-center">
                      <SecondaryButton
                        disabled={!(selectedItems.length > 0)}
                        type="submit"
                        style={{ marginTop: 10 }}
                        id="id-mySuppliersRegister-button-save">
                        {getTranslateByKey('information.save')}
                      </SecondaryButton>
                    </div>
                  </>
                )}
              </form>
            </AccordionDetails>
          </Accordion>
        </KogModalSideComponent>
      )}
      <ModalConfirmationReason closeByOverlay={false} />
    </>
  );
}

export default MySuppliersRegister;
